import { graphql } from '$houdini'
import { refreshSessionLicenseInfo } from 'stores/session'
import { applySessionToStore } from 'utility/session-helper'
import buildApiUrl from 'utility/build-api-url'

/**
 * Redirect the browser to the BFF login endpoint.
 * The BFF (oauth server) owns the full OAuth dance — PKCE generation,
 * code exchange, and token storage. Enterprise-web never sees the token.
 */
export function startOAuthFlow(): void {
	const { microservicehost } = buildApiUrl()
	window.location.href = `${microservicehost}/oauth/bff/login`
}

/**
 * Ask the BFF to clear its HttpOnly access-token cookie.
 * Call this instead of the legacy closeSession GraphQL mutation when in OAuth mode.
 */
export async function endOAuthSession(): Promise<void> {
	const { microservicehost } = buildApiUrl()
	await fetch(`${microservicehost}/oauth/bff/logout`, {
		method: 'POST',
		credentials: 'include',
	})
}

/**
 * After the BFF sets the HttpOnly cookie and redirects back to the SPA,
 * load the user session from the GraphQL API (the cookie is sent automatically)
 * and populate sessionStore.
 *
 * Throws if the session query fails or returns incomplete data.
 */
export async function populateSessionFromCookie(): Promise<void> {
	let data: Awaited<ReturnType<typeof oauthSessionQuery.fetch>>['data'] | null = null
	try {
		data = (await oauthSessionQuery.fetch({ policy: 'NetworkOnly' })).data
	} catch (e) {
		console.error('populateSessionFromCookie: failed to fetch session with cookie auth', e)
		throw new Error('Failed to load session from API')
	}
	if (!data?.session) {
		throw new Error('Failed to load session from API')
	}

	const { userAccount, companyInfo, storeId, permissions, ...sessionBase } = data.session

	if (!userAccount || !companyInfo?.id || !storeId) {
		throw new Error('Incomplete session data from API')
	}

	applySessionToStore({
		sessionBase,
		userAccount,
		sessionPermissions: permissions,
		companyId: companyInfo.id,
		storeId,
		authToken: 'bff-cookie',
	})

	await refreshSessionLicenseInfo()
}

const oauthSessionQuery = graphql(`
	query OAuthSession {
		session {
			id
			storeId
			accessStatus
			accessType
			companyInfo {
				id
			}
			...Permissions
			userAccount {
				...SessionUserAccount
			}
		}
	}
`)
